import { Maximize2, Minimize2, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Map as MapComponent, MapRef } from '@/components/territories/map/Map';
import { TerritoriesByState } from '@/components/territories/TerritoriesByState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TERRITORIES_API_URL } from '@/constants/apis';
import { configuration } from '@/constants/config';
import { US_STATES } from '@/constants/territories/states';
import { US_MAP_CENTER } from '@/constants/territories/variables';
import { apiManager } from '@/helpers/api.manager';
import { toast } from '@/hooks/ui/use-toast';
import { useGetZorTerritorySettings } from '@/hooks/zor-territory-settings/useGetZorTerritorySettings';
import { cn } from '@/lib/ui/utils';
import { useTerritoryStore } from '@/stores/useTerritoryStore';
import { useZorTerritorySettingsStore } from '@/stores/useZorTerritorySettingsStore';
import { Territory } from '@/types/territories/territory.type';

interface MapCenter {
  lat: number;
  lng: number;
}

const DEFAULT_ZOOM = 4;

const useDebounce = <T,>(value: T, delay: number = 500): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const getFirstMatchingStateCode = (value: string): string => {
  if (!value.trim()) return '';

  const lowerSearch = value.toLowerCase();

  const match = Object.entries(US_STATES).find(([, fullName]) =>
    fullName.toLowerCase().includes(lowerSearch)
  );

  return match?.[0] || value;
};

export const DashboardTerritories = () => {
  // refs
  const mapRef = useRef<MapRef>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // state
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [loadingTerritories, setLoadingTerritories] = useState(true);
  const debouncedSearchValue = useDebounce(searchValue);

  // custom hooks
  const {
    setTerritories,
    focusedTerritory,
    setFocusedTerritory,
    setSelectedTerritory,
  } = useTerritoryStore();

  const { setZorTerritorySettings } = useZorTerritorySettingsStore();

  // ZorTerritorySettings data
  const {
    result: zorTerritorySettingsResult,
    isError: isTerritorySettingsError,
    isSuccess: isTerritorySettingsSuccess,
  } = useGetZorTerritorySettings();

  // Set ZorTerritorySettings data to store
  useEffect(() => {
    if (zorTerritorySettingsResult?.data && isTerritorySettingsSuccess) {
      setZorTerritorySettings(zorTerritorySettingsResult.data);
    }
  }, [zorTerritorySettingsResult, setZorTerritorySettings]);
  useEffect(() => {
    if (isTerritorySettingsError) {
      toast({
        title: 'Error loading zor territory settings',
        variant: 'destructive',
      });
    }
  }, [isTerritorySettingsError]);

  // derived values
  const mapCenter: MapCenter = US_MAP_CENTER;

  // event handlers
  const fetchTerritories = async (): Promise<void> => {
    setLoadingTerritories(true);
    try {
      const searchParam = getFirstMatchingStateCode(debouncedSearchValue);
      const response = await apiManager.get(TERRITORIES_API_URL, {
        baseURL: configuration.TERRITORIES_API_URL,
        headers: { 'Content-Type': 'application/json' },
        params: {
          ...(searchParam && {
            search: searchParam,
          }),
        },
      });

      if (response.status !== 200) {
        toast({
          title: 'Error loading territories',
          variant: 'destructive',
        });
        return;
      }

      setTerritories(response.data.data);
    } catch {
      toast({
        title: 'Error loading territories',
        variant: 'destructive',
      });
    } finally {
      setLoadingTerritories(false);
    }
  };

  const handleToggleExpand = (): void => {
    setIsExpanded(!isExpanded);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchValue(e.target.value);
  };

  const handleTerritoryClick = (territory: Territory): void => {
    setFocusedTerritory(territory);

    mapContainerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    if (mapRef.current && territory.centroid) {
      mapRef.current.flyTo({
        lat: territory.centroid.coordinates[1],
        lng: territory.centroid.coordinates[0],
      });
    }

    setTimeout(() => {
      setSelectedTerritory(territory);
    }, 800);
  };

  // effects
  useEffect(() => {
    void fetchTerritories();
  }, [debouncedSearchValue]);

  useEffect(() => {
    if (focusedTerritory && mapRef.current) {
      mapRef.current.flyTo({
        lat: focusedTerritory.centroid.coordinates[1],
        lng: focusedTerritory.centroid.coordinates[0],
      });
    } else if (mapRef.current) {
      mapRef.current.flyTo(US_MAP_CENTER, DEFAULT_ZOOM);
    }
  }, [focusedTerritory]);

  return (
    <div className="space-y-6">
      <div className="flex w-full flex-col justify-between gap-4 md:flex-row">
        <div className="territories-page-header max-w-md">
          <h2 className="as-strong">Territories</h2>
          <p className="as-title-02 mt-2 font-light text-typo-gray-tertiary">
            Information about territories set by franchisors!
          </p>
        </div>
      </div>
      <div className="space-y-6">
        <div
          ref={mapContainerRef}
          className={cn(
            'territories-map-container relative rounded-lg transition-all duration-300',
            isExpanded ? 'h-[80vh]' : 'h-80'
          )}
        >
          <MapComponent
            ref={mapRef}
            zoom={4}
            center={mapCenter}
            onTerritoryClick={handleTerritoryClick}
          />
          <Button
            className="map-expand-button absolute right-2 top-2"
            onClick={handleToggleExpand}
          >
            {isExpanded ? <Minimize2 /> : <Maximize2 />}
            {isExpanded ? 'Minimize' : 'Expand'}
          </Button>
        </div>
        <div className="flex items-center justify-between">
          <div className="relative flex w-full items-center">
            <Search size={16} className="absolute left-3 text-gray-500" />
            <Input
              type="text"
              value={searchValue}
              className="territories-search-input pl-8"
              placeholder="Search by territory number, state or zip"
              onChange={handleSearchChange}
            />
          </div>
        </div>
        <div className="territories-by-state-container space-y-1.5">
          <TerritoriesByState
            loading={loadingTerritories}
            onTerritoryClick={handleTerritoryClick}
          />
        </div>
      </div>
    </div>
  );
};
